"use client";

import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import type { FeaturedTalent } from "@/lib/sanity/featured-talents";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("app/plaza/components/featured-builders-section.tsx");


interface FeaturedBuildersResponse {
	data: FeaturedTalent[];
	sanityConfigured: boolean;
}

const LoadingBuilderCard = () => (
	<div className="flex w-[160px] flex-shrink-0 flex-col items-center gap-2 rounded-xl border border-light-border dark:border-dark-border bg-light-background dark:bg-dark-background p-3">
		<div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
		<div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
		<div className="h-3 w-16 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
	</div>
);

const BuilderCard = ({
	builder,
	href,
}: {
	builder: FeaturedTalent;
	href: string;
}) => {
	const ratingText = useMemo(() => {
		if (typeof builder.rating !== "number" || builder.rating <= 0) return null;
		return builder.rating.toFixed(1);
	}, [builder.rating]);

	return (
		<Link
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="group relative flex w-[160px] lg:w-[180px] flex-shrink-0 flex-col items-center justify-center overflow-hidden rounded-xl border border-light-border/70 bg-light-background/60 p-3 shadow-sm transition-all hover:scale-105 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-light-primary/60 dark:border-dark-border/70 dark:bg-dark-background/60"
		>
			<div className="relative h-12 w-12 overflow-hidden rounded-full border border-light-border/50 dark:border-dark-border/50">
				{builder.avatar ? (
					<Image
						src={builder.avatar}
						alt={builder.name}
						fill
						sizes="48px"
						className="object-cover"
					/>
				) : (
					<div className="flex h-full w-full items-center justify-center bg-gray-100 text-sm font-bold text-gray-500 dark:bg-gray-800">
						{builder.name?.slice(0, 1) || "?"}
					</div>
				)}
			</div>

			<div className="mt-2 text-center w-full px-1">
				<h4
					className="truncate text-sm font-semibold text-light-text dark:text-dark-text"
					title={builder.name}
				>
					{builder.name}
				</h4>
				<div className="flex items-center justify-center gap-1 mt-1">
					{ratingText && (
						<div className="flex items-center gap-0.5 text-xs font-medium text-amber-500">
							<Star className="h-3 w-3 fill-current" />
							<span>{ratingText}</span>
						</div>
					)}
					{builder.title && (
						<>
							{ratingText && (
								<span className="text-gray-300 dark:text-gray-600">|</span>
							)}
							<p
								className="truncate text-xs text-light-accent dark:text-dark-accent max-w-[80px]"
								title={builder.title}
							>
								{builder.title}
							</p>
						</>
					)}
				</div>
			</div>
		</Link>
	);
};

export function FeaturedBuildersSection() {
	const { getTranslation } = useLanguage();
	const [builders, setBuilders] = useState<FeaturedTalent[]>([]);
	const [loading, setLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: "start",
		containScroll: "trimSnaps",
		dragFree: true,
	});

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev();
	}, [emblaApi]);

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext();
	}, [emblaApi]);

	useEffect(() => {
		let active = true;

		const loadBuilders = async () => {
			try {
				const response = await fetch("/api/plaza/featured-builders", {
					method: "GET",
					cache: "no-store",
				});

				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}

				const data: FeaturedBuildersResponse = await response.json();
				if (active) {
					setBuilders(data.data);
					setHasError(false);
				}
			} catch (error) {
				uiLog.error("Failed to load featured builders", error);
				if (active) {
					setHasError(true);
				}
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		};

		loadBuilders();

		return () => {
			active = false;
		};
	}, []);

	const displayedBuilders = useMemo(
		() =>
			builders
				.filter((builder) => builder.verified && builder.available)
				.slice(0, 15),
		[builders],
	);

	return (
		<Card className="rounded-2xl border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface shadow-md overflow-hidden">
			<CardHeader className="flex flex-row items-center justify-between gap-4 py-4 px-6">
				<CardTitle className="text-light-text dark:text-dark-text text-lg font-bold flex items-center gap-2">
					{getTranslation("plaza.featuredBuildersTitle")}
					<Badge
						variant="secondary"
						className="hidden sm:inline-flex text-[10px] h-5 px-1.5 font-normal bg-light-primary/10 text-light-primary dark:bg-dark-primary/10 dark:text-dark-primary"
					>
						{getTranslation("plaza.verified")}
					</Badge>
				</CardTitle>
				<div className="flex items-center gap-3">
					<div className="hidden lg:flex gap-1">
						<Button
							variant="outline"
							size="icon"
							className="h-7 w-7 rounded-full border-light-border dark:border-dark-border"
							onClick={scrollPrev}
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<Button
							variant="outline"
							size="icon"
							className="h-7 w-7 rounded-full border-light-border dark:border-dark-border"
							onClick={scrollNext}
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
					<Link
						href="https://www.web3build.vip"
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm font-medium text-light-primary transition-opacity hover:opacity-70 dark:text-dark-primary whitespace-nowrap"
					>
						{getTranslation("plaza.viewMore")}
					</Link>
				</div>
			</CardHeader>

			<CardContent className="p-0 pb-4">
				<div className="relative px-6">
					<div className="overflow-hidden" ref={emblaRef}>
						<div className="flex gap-3 py-1">
							{loading ? (
								Array.from({ length: 6 }).map((_, index) => (
									<LoadingBuilderCard key={index} />
								))
							) : displayedBuilders.length > 0 ? (
								displayedBuilders.map((builder) => {
									const href =
										builder.id && !builder.id.startsWith("placeholder-")
											? `https://www.web3build.vip/talents/${builder.id}`
											: "https://www.web3build.vip/talents";

									return (
										<BuilderCard
											key={builder.id}
											builder={builder}
											href={href}
										/>
									);
								})
							) : (
								<div className="w-full text-center py-4 text-sm text-gray-500">
									{getTranslation("plaza.noFeaturedBuilders")}
								</div>
							)}
						</div>
					</div>

					{/* Mobile swipe hint gradient */}
					<div className="lg:hidden pointer-events-none absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-light-surface to-transparent dark:from-dark-surface" />
				</div>

				{hasError && (
					<div className="mx-6 mt-2 rounded-lg bg-status-error/10 p-2 text-center text-xs text-status-error">
						{getTranslation("plaza.loadFeaturedBuildersError")}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
